import * as React from "react"
import { PaginationType } from "@/types"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute, redirect } from "@tanstack/react-router"
import type { DateRange } from "react-day-picker"
import { useReactToPrint } from "react-to-print"

import {
  BillingAdjustmentOverviewType,
  BillingAdjustmentType,
  BillingOverviewType,
} from "@/types/"
import { BillingAdjustmentDailyCountType } from "@/types/billing"
import { meterDailyAnalyticsCountType } from "@/types/meter"
import { meterChartConfig } from "@/config/charts"
import { canViewThisPage } from "@/lib/api/auth"
import {
  getBillingAdjustmentDailyCount,
  getBillingAdjustmentList,
  getBillingAdjustmentOverview,
  getBillingAdjustmentPieData,
  getBillingOverview,
} from "@/lib/api/billing"
import { getMeterDailyAnalyticsCount } from "@/lib/api/meter"
import { getStatusStyle } from "@/lib/statusStyles"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Skeleton } from "@/components/ui/skeleton"
import { BarChartStacked } from "@/components/charts/barchart-stacked"
import { PieChartComponent } from "@/components/charts/piechart"
import { DataTable } from "@/components/data-table"
import { DateRangePicker } from "@/components/date-range-picker"
import { Header } from "@/components/header"
import { Icons } from "@/components/icons"
import SelectTimeRange, {
  timeRangeOptions,
} from "@/components/select-time-range"
import { billingAdjustmentColumns } from "@/components/table/columns/billing-adjustment-columns"
import { ExportOperationModal } from "@/components/table/operations/export-operations"

export const Route = createFileRoute("/_home/billing-adjustment")({
  beforeLoad: () => {
    if (canViewThisPage("himo_billing.view_billingadjustment")) {
      return
    }

    return redirect({ to: "/access-denied" })
  },
  component: BillingAdjustmentPage,
})

function BillingAdjustmentPage() {
  const [showExportModal, setShowExportModal] = React.useState<boolean>(false)
  const [defaultPage, setDefaultPage] = React.useState<number>(10)
  const [page, setPage] = React.useState<number>(1)
  const [search, setSearch] = React.useState<string>("")
  const [nextPageUrl, setNextPageUrl] = React.useState<string | null>(null)
  const [prevPageUrl, setPrevPageUrl] = React.useState<string | null>(null)
  const [timeRange, setTimeRange] = React.useState(timeRangeOptions()[0])
  const [billingAdjusmentPieDayRange, setBillingAdjusmentPieDayRange] =
    React.useState(timeRangeOptions()[7])
  const [date, setDate] = React.useState<DateRange>({
    from: new Date(new Date().setDate(new Date().getDate() - 7)), // 7 days ago
    to: new Date(), // Today
  })
  const [billingAdjustmentDailyCountdate, setBillingAdjustmentDailyCountDate] =
    React.useState<DateRange>({
      from: new Date(new Date().setDate(new Date().getDate() - 7)), // 7 days ago
      to: new Date(), // Today
    })

  const [billingAdjustmentPieAnalytics, setBillingAdjustmentPieAnalyticsDate] =
    React.useState<DateRange>({
      from: new Date(new Date().setDate(new Date().getDate() - 7)), // 7 days ago
      to: new Date(), // Today
    })

  const {
    data: billingAdjustmentDailyCount,
    isLoading: billingAdjustmentDailyCountIsLoading,
  } = useQuery<BillingAdjustmentDailyCountType>({
    queryKey: [
      "billingAdjustmentDailyCount",
      billingAdjustmentDailyCountdate.from?.toISOString(),
      billingAdjustmentDailyCountdate.to?.toISOString(),
    ],
    queryFn: async () => {
      const from = billingAdjustmentDailyCountdate.from ?? new Date()
      const to = billingAdjustmentDailyCountdate.to ?? new Date()
      return await getBillingAdjustmentDailyCount(
        from.toISOString(),
        to.toISOString(),
      )
    },
  })

  const {
    data: billingAdjustment,
    isError: isBillingError,
    isLoading: isBillingLoading,
  } = useQuery<PaginationType<BillingAdjustmentType>>({
    queryKey: ["billingAdjustment", defaultPage, page, search],
    queryFn: () => getBillingAdjustmentList(defaultPage, page, search),
  })

  React.useEffect(() => {
    if (billingAdjustment) {
      setNextPageUrl(billingAdjustment.next)
      setPrevPageUrl(billingAdjustment.previous)
    }
  }, [billingAdjustment])

  const contentRef = React.useRef<HTMLDivElement>(null)
  const reactToPrintFn = useReactToPrint({ contentRef })

  // to be replace by billing adjustment overview api
  const {
    data: billingAdjustmentOverview,
    isLoading: isBillingAdjustmentLoading,
  } = useQuery<BillingAdjustmentOverviewType>({
    queryKey: ["billingAdjustmentOverview", timeRange.value],
    queryFn: () => getBillingAdjustmentOverview(Number(timeRange.value)),
  })

  const {
    data: billingAdjustmentPieChartData,
    isLoading: isBillingAdjustmentPieChartLoading,
  } = useQuery({
    queryKey: [
      "billingAdjustmentPieChart",
      billingAdjustmentPieAnalytics.from?.toISOString(),
      billingAdjustmentPieAnalytics.to?.toISOString(),
    ],
    queryFn: async () => {
      const from = billingAdjustmentPieAnalytics.from ?? new Date()
      const to = billingAdjustmentPieAnalytics.to ?? new Date()
      return await getBillingAdjustmentPieData(
        from.toISOString(),
        to.toISOString(),
      )
    },
  })

  const renderOverviewCard = (
    title: string,
    icon: keyof typeof Icons,
    data?: {
      total: number | string
      percentage: number | null
      status: string
    },
  ) => {
    const IconComponent = Icons[icon]
    const {
      color,
      bgColor,
      icon: StatusIcon,
    } = getStatusStyle(data?.status ?? "", data?.percentage ?? null)

    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="flex gap-2 text-[13px] font-medium text-muted-foreground">
            {["Total Consumers", "Active Consumers"].includes(title) && (
              <Icons.person className="h-[18px] w-[18px]" />
            )}
            {[
              "Total Payments",
              "Total Billing",
              "Total Bill Adjustment",
            ].includes(title) && (
              <img
                src="peso-sign.png"
                alt="icon"
                className="h-[18px] w-[18px]"
              />
            )}
            {["Total Meters"].includes(title) && (
              <Icons.gauge className="h-[20px] w-[20px] text-[#00B6AE]" />
            )}
            {["Before Due", "Pending Bill Adjustment"].includes(title) && (
              <Icons.circle className="h-[20px] w-[20px] rounded-full bg-yellow-500" />
            )}
            {["Due Date", "Approved Bill Adjustment"].includes(title) && (
              <Icons.circle className="h-[20px] w-[20px] rounded-full bg-primary" />
            )}
            {["Over Due"].includes(title) && (
              <Icons.circle className="h-[20px] w-[20px] rounded-full bg-orange-500" />
            )}
            {["Disconnection", "Denied Bill Adjustment"].includes(title) && (
              <Icons.circle className="h-[20px] w-[20px] rounded-full bg-red-600" />
            )}
            <p className="line-height-[14px] font-worksans font-bold tracking-wider">
              {title}
            </p>
          </CardTitle>
        </CardHeader>
        <CardContent className="text-2xl font-bold">
          {isBillingAdjustmentLoading ? (
            <Skeleton className="h-8 w-24" />
          ) : data?.total !== undefined ? (
            <div className="flex flex-row items-center gap-2">
              <h1 className="font-semibold">
                {title === "Total Payments" ? `PHP ${data.total}` : data.total}
              </h1>
              {data.status !== "New" && (
                <span
                  className={`rounded-sm px-1 text-[13px] font-normal ${color} ${bgColor}`}
                >
                  {data.status !== "Flat" && data.percentage !== null
                    ? `${data.percentage.toFixed(2)}%`
                    : ""}

                  {StatusIcon && (
                    <span
                      className={
                        data.status === "Flat"
                          ? "flex items-center"
                          : "inline-block px-1"
                      }
                    >
                      {StatusIcon}
                    </span>
                  )}
                </span>
              )}
            </div>
          ) : (
            "N/A"
          )}
        </CardContent>
      </Card>
    )
  }

  return (
    <main className="space-y-4 p-4">
      <div className="flex w-full justify-between pt-4">
        <div>
          <Header>Billing Adjustment</Header>
        </div>
        <div className="flex items-center gap-3">
          <Label>Set view</Label>
          <SelectTimeRange range={timeRange} setRange={setTimeRange} />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-2 md:grid-cols-3 md:items-stretch">
        <div className="grid-cols-4 gap-4 space-y-4 overflow-visible font-worksans md:col-span-4 md:grid md:space-y-0">
          {renderOverviewCard(
            "Total Bill Adjustment",
            "peso",
            billingAdjustmentOverview?.total_bill_adjustments,
          )}
          {renderOverviewCard(
            "Pending Bill Adjustment",
            "peso",
            billingAdjustmentOverview?.pending_bill_adjustments,
          )}
          {renderOverviewCard(
            "Approved Bill Adjustment",
            "user",
            billingAdjustmentOverview?.approved_bill_adjustments,
          )}
          {renderOverviewCard(
            "Denied Bill Adjustment",
            "user",
            billingAdjustmentOverview?.denied_bill_adjustments,
          )}
        </div>
      </div>
      <div className="grid grid-cols-1 gap-2 md:grid-cols-3 lg:grid-cols-3">
        <Card className="col-span-2">
          <CardHeader className="flex flex-col items-center justify-between py-2 lg:flex-row">
            <CardTitle>Billing Adjustment Analytics</CardTitle>
            <div>
              <DateRangePicker
                date={billingAdjustmentDailyCountdate}
                setDate={setBillingAdjustmentDailyCountDate}
              />
            </div>
          </CardHeader>
          <CardContent>
            {billingAdjustmentDailyCountIsLoading ? (
              <div className="flex h-[240px] items-center justify-center">
                <Skeleton className="h-full w-full" />
              </div>
            ) : billingAdjustmentDailyCount ? (
              <BarChartStacked
                data={billingAdjustmentDailyCount}
                //config={meterChartConfig}
              />
            ) : (
              <div className="flex h-[240px] items-center justify-center text-gray-500">
                No data available
              </div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-col items-center justify-between py-2 lg:flex-row">
            <CardTitle>Billing Adjustment</CardTitle>

            <div className="flex items-center gap-3">
              <DateRangePicker
                date={billingAdjustmentPieAnalytics}
                setDate={setBillingAdjustmentPieAnalyticsDate}
              />
            </div>
          </CardHeader>
          <CardContent>
            {isBillingAdjustmentPieChartLoading ? (
              <div className="flex h-[240px] items-center justify-center">
                <Skeleton className="h-full w-full" />
              </div>
            ) : billingAdjustmentPieChartData ? (
              <div className="flex h-full items-center justify-center">
                <PieChartComponent
                  data={billingAdjustmentPieChartData}
                  dataKey="count"
                  nameKey="adjustment_status"
                  className="height:1000px"
                />
              </div>
            ) : (
              <div className="flex h-[240px] items-center justify-center text-gray-500">
                No data available
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      <div className="grid grid-cols-1 md:items-stretch">
        <DataTable
          columns={billingAdjustmentColumns}
          data={billingAdjustment?.results ?? []}
          search={{
            label: "Billing adjustment",
            value: search,
            onChange: setSearch,
          }}
          defaultPage={defaultPage}
          setDefaultPage={setDefaultPage}
          nextPageUrl={nextPageUrl}
          prevPageUrl={prevPageUrl}
          page={page}
          setPage={setPage}
          totalCount={billingAdjustment?.count}
          isLoading={isBillingLoading}
          isError={isBillingError}
        >
          <div
            ref={contentRef}
            className="flex w-full max-w-full justify-end print:p-2"
          >
            <Button
              onClick={(e) => reactToPrintFn()}
              variant="outline"
              className="mx-1 bg-primary text-white hover:bg-primary hover:text-white print:hidden"
            >
              <Icons.download className="mr-2 h-4 w-4" />
              Print Report
            </Button>
            <ExportOperationModal
              tableData={billingAdjustment?.results ?? [{}]}
              fileName="Billing Adjustment"
              showExportModal={showExportModal}
              setShowExportModal={setShowExportModal}
            />
          </div>
        </DataTable>
      </div>
    </main>
  )
}
